import { Injectable } from "@angular/core";
import { UserRepository } from "./user-repository";
import { User } from "./user";
import { Channel, ChannelInfo } from "./channel";
import { Subject } from "rxjs";
import { ChannelRepository } from "./channel-repository";
import moment from "moment";

const CURRENT_CHANNEL_ID_STORAGE_KEY = "currentChannelId";
const CURRENT_TIMEZONE_STORAGE_KEY = "currentTimezone";

@Injectable()
export class UserService {
  public currentChannelId: number;
  currentChannelIdUpdated = new Subject<number>();
  currentChannelSaved = new Subject<Channel>();
  private channelInfoRequests = new Map<number, Promise<ChannelInfo>>();

  constructor(
    private userRepository: UserRepository,
    private channelRepository: ChannelRepository,
  ) {}

  init() {
    const channelId = localStorage.getItem(CURRENT_CHANNEL_ID_STORAGE_KEY);
    const timezone = localStorage.getItem(CURRENT_TIMEZONE_STORAGE_KEY);
    if (timezone && channelId) {
      this.currentChannelId = Number(channelId);
      moment.tz.setDefault(timezone);
    }

    this.channelRepository.channelSaved.subscribe((channel) => {
      if (channel.id === this.currentChannelId) {
        this.currentChannelSaved.next(channel);
      }
    });
  }

  getCurrentChannel(refreshCache = false): Promise<Channel> {
    if (!this.currentChannelId) {
      return Promise.resolve(null);
    }
    if (refreshCache) {
      return this.channelRepository.refreshCache().then(() => {
        return this.channelRepository.getChannelById(this.currentChannelId);
      });
    } else {
      return this.channelRepository.getChannelById(this.currentChannelId);
    }
  }

  getCurrentChannelInfo(refreshCache = false): Promise<ChannelInfo> {
    return this.getCurrentChannel(refreshCache).then((channel) => {
      if (!channel) {
        return null;
      }

      const channelId = channel.id;
      const existingRequest = this.channelInfoRequests.get(channelId);
      if (existingRequest) {
        return existingRequest;
      }

      const request = this.channelRepository
        .getChannelInfoById(channelId)
        .finally(() => {
          if (this.channelInfoRequests.get(channelId) === request) {
            this.channelInfoRequests.delete(channelId);
          }
        });

      this.channelInfoRequests.set(channelId, request);
      return request;
    });
  }

  setCurrentChannel(channel: Channel) {
    this.currentChannelId = channel.id;
    const timezone = this.getTimezoneValueById(channel.timezoneId);
    localStorage.setItem(CURRENT_CHANNEL_ID_STORAGE_KEY, channel.id.toString());
    localStorage.setItem(CURRENT_TIMEZONE_STORAGE_KEY, timezone);
    moment.tz.setDefault(timezone);
    this.currentChannelIdUpdated.next(channel.id);
  }

  getCurrentUser(): User {
    return this.userRepository.getCurrentUser();
  }

  refreshCurrentUser(): Promise<void> {
    return this.userRepository.init();
  }

  getTimezoneValueById(id: number): string {
    return this.userRepository.getTimezoneValueById(id);
  }

  getTimezoneOffset(id: number, timestamp?: number): number {
    const timezoneValue = this.getTimezoneValueById(id);
    const m = moment(timestamp);
    // @ts-ignore
    return moment.tz.zone(timezoneValue).utcOffset(m);
  }

  /**
   * Timezone used for display (same as header clock). From current channel or browser.
   * Use for formatting dates and for interpreting user-entered date/time as local.
   */
  getDisplayTimezone(): string {
    const stored = localStorage.getItem(CURRENT_TIMEZONE_STORAGE_KEY)?.trim();
    if (stored) return stored;
    try {
      const resolved = typeof Intl !== "undefined" && Intl.DateTimeFormat?.().resolvedOptions?.().timeZone;
      if (resolved?.trim()) return resolved;
    } catch {
      // ignore
    }
    return "UTC";
  }
}
